import type { LayerSpecification, StyleSpecification } from 'maplibre-gl'
import {
  SLOT_BAND,
  SLOT_HIGHLIGHT,
  SLOT_DOT,
  SLOT_LABEL,
  SLOT_LINE,
  SLOT_RELIEF,
  SLOT_TINT,
  slotLayer,
} from '../components/map/slots'
import italicUrl from '../fonts/atlas-italic.woff2?url'
import romanUrl from '../fonts/atlas-roman.woff2?url'
import {
  MAP_FONT_ITALIC,
  MAP_FONT_RANGES,
  MAP_FONT_ROMAN,
  PLAIN_BACKGROUND_COLOR,
  VINEYARD_COLOR,
  VINEYARD_EDGE_COLOR,
  VINEYARD_MIN_ZOOM,
} from './map_config'

/**
 * The atlas's own basemap: a printed wine-atlas plate rather than a street map,
 * laid on real ground.
 *
 * Chalk-coloured land, pale bottle-glass water with a coast hairline, the vines
 * themselves drawn on the ground, rivers a reader can follow, and nothing else
 * loud enough to compete with an appellation. 34 layers, seven of which draw
 * nothing (the slots); OpenFreeMap's hosted `liberty`, which this replaced, has
 * 111.
 *
 * **It is an object in the map chunk, not a URL.** `new Map({ style })` is
 * handed this value, so the style can no longer fail to download, one request
 * leaves the critical chain (style → TileJSON → tiles becomes TileJSON → tiles),
 * and `style.load` fires about as soon as the map is constructed. It is imported
 * from use_maplibre.ts only — never from lib/map_config.ts, which the layout
 * imports statically and whose header forbids exactly this weight.
 *
 * **Everything on the boot path still resolves against OpenFreeMap and this
 * app's own origin, and nothing else** (R12, KTD8): the `planet` vector
 * TileJSON, and two font files of this app's own. No key, no registration, and
 * the attribution in `BASEMAP_ATTRIBUTION` is unchanged because the tiles, the
 * schema and the data are the same three parties.
 *
 * - **No sprite.** Nothing here draws a shield or a pattern, so the two sprite
 *   requests `liberty` made are gone — and with them city dots, which need one.
 *   A label centred on its place reads fine at atlas zooms. The one icon in the
 *   style, the summit mark, is sixteen pixels the map draws for itself
 *   (components/map/peak_mark.ts): an image with no request behind it.
 * - **No raster IN THE STYLE.** `liberty`'s Natural Earth hillshade was 1.8 MB
 *   on the opening world view alone, cold grey, and exists only below zoom 7.
 *   The plate does have a relief now — this app's own, pre-toned, a few
 *   kilobytes an image — but its source is not here: the map adds it after its
 *   first idle (components/map/relief.ts), under `SLOT_RELIEF`, so the first
 *   paint asks for exactly what it asked for before there was one. Wood, town
 *   and ice are drawn part-transparent for it, so a slope reads through them.
 * - **Two fontstacks, each named alone, and two font files behind them.** The
 *   glyph endpoint serves Noto Sans Regular, Bold and Italic and answers 404 to
 *   everything else, including any comma-joined stack. Every label names one of
 *   the first two; `font-faces` below says both are drawn from this app's two
 *   static cuts of Fraunces for the codepoints those cover, and the endpoint is
 *   asked only for what they do not. Roman is for a point, italic for an area
 *   or a natural feature; hierarchy otherwise comes from size, case,
 *   letter-spacing and ink, not from a third face.
 * - **One line per label, English first.** `liberty` draws the Latin name over
 *   the local-script one, which doubles label height and asks for twenty glyph
 *   ranges on the world view. This asks for none on a Latin-script view.
 * - **No buildings, POIs, shields, rail, aeroways or parks.** `MAX_ZOOM` is 14,
 *   where OpenMapTiles has barely begun to carry buildings, and none of them
 *   says anything about wine. Leaving their source-layers unnamed also means the
 *   worker never builds buckets for them.
 *
 * The layer objects are typed loosely and cast (`layer()` below): MapLibre's
 * expression types reject perfectly valid nested expressions, and what actually
 * holds this file to the specification is atlas_style.test.ts, which runs the
 * style-spec validator over the finished object and checks every fontstack and
 * every host. A cast the validator has passed is worth more than a type it has
 * not.
 */

const OPENFREEMAP = 'https://tiles.openfreemap.org'

// The id the vector source is registered under. Exported because the map has to
// tell a basemap failure from any other error, and an error names its source.
export const BASEMAP_SOURCE_ID = 'openmaptiles'

// Meridians and parallels, as sixteen lines the style carries itself.
const GRATICULE_SOURCE_ID = 'atlas-graticule'

// The summit mark. The style only names it; the map registers the pixels
// (components/map/peak_mark.ts), because a style object cannot carry an image
// and a sprite would be a request.
export const PEAK_IMAGE_ID = 'atlas-peak'

const GLYPHS = `${OPENFREEMAP}/fonts/{fontstack}/{range}.pbf`

// English first, then the Latin-script local name, then whatever there is. A
// place with no Latin name at all still gets its own script rather than nothing.
const NAME = ['coalesce', ['get', 'name_en'], ['get', 'name:latin'], ['get', 'name']]

const LINES = ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false]
const POINTS = ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]
const NOT_TUNNEL = ['!=', ['get', 'brunnel'], 'tunnel']

const zoomRamp = (...stops: unknown[]) => ['interpolate', ['linear'], ['zoom'], ...stops]

// Where a place name may go, in the order it is tried: on its point, then to
// one side of it. A name used to be centred on its point or dropped. With
// winery dots keeping their own room on the map (`SLOT_DOT`), "dropped" was
// the usual answer in exactly the towns a wine map is about — Los Olivos is a
// crossroads with twenty producers on it — so a name now steps aside instead.
// The cost is up to five placement attempts for a name that collides.
const STEP_ASIDE = ['literal', ['center', [0, 0], 'top', [0, 0.55], 'bottom', [0, -0.55], 'left', [0.6, 0], 'right', [-0.6, 0]]]

/**
 * Warm-neutral paper, bottle-glass water, and label inks from the same family
 * as the chrome's `--color-ink` and `--color-ink-soft`, so the map and the
 * labels laid on it read as one object.
 *
 * `land` is the chrome's own neutral paper rather than a cream page, and the
 * shell behind the canvas is the same value, so the map does not flash a second
 * grey while it loads. It is also, exactly, the tone flat ground is baked to in
 * the relief: adding the hillshade moves nothing that was flat. `water` is pale
 * bottle glass, not the blue of a road map.
 *
 * Against the palette this replaced, everything that sits ON the ground went a
 * step deeper, because the ground is no longer a flat sheet: a river in the old
 * `waterLine` was a scratch on a hillside, sage vines disappeared into a lit
 * slope, and a label ink chosen against flat chalk lost a grade against a
 * shaded one.
 *
 * A palette is a parameter of `atlasStyle` rather than a set of literals in the
 * layers so that a dark plate is a second object and not a second file. None
 * ships: the chrome has no dark theme and the claret ink turns to oxblood on a
 * dark ground.
 */
export const ATLAS_PALETTE = {
  land: PLAIN_BACKGROUND_COLOR,
  ice: '#faf9f6',
  wood: '#d9e0c8',
  vineyard: VINEYARD_COLOR,
  vineyardLine: VINEYARD_EDGE_COLOR,
  urban: '#e9e5dd',
  water: '#cbd9d4',
  // The hairline where water meets land.
  coast: '#9db9b0',
  // Rivers: the same glass two steps deeper, so a river is a line to follow.
  waterLine: '#8fb4aa',
  graticule: '#5f8a80',
  road: '#fdfcf9',
  roadCasing: '#d2c9bb',
  roadFar: '#d3cabd',
  roadMotorwayFar: '#c6bbac',
  boundary: '#a2968f',
  boundaryState: '#bdb3ab',
  labelCountry: '#5e4f55',
  labelState: '#a2959a',
  labelCity: '#3b2a31',
  labelTown: '#56444b',
  labelVillage: '#716067',
  labelRoad: '#9c9091',
  labelWater: '#4f7f74',
  labelPeak: '#75655b',
  // The halo is the paper, not white. A white halo on a chalk map is what makes
  // a label look pasted on.
  halo: 'rgba(243,241,236,0.94)',
  waterHalo: 'rgba(203,217,212,0.82)',
}

export type AtlasPalette = typeof ATLAS_PALETTE

// Road widths in px, as [zoom, width] stops per class, interpolated at base 1.4.
// ONE table feeds the stroke and its casing, so the two cannot drift apart.
const ROAD_BASE = 1.4
const ROAD_WIDTHS: Record<string, [number, number][]> = {
  motorway: [[6, 0.35], [9, 0.9], [12, 2.1], [14, 4.2]],
  trunk: [[6, 0.35], [9, 0.9], [12, 2.1], [14, 4.2]],
  primary: [[8, 0.3], [11, 1], [14, 3.2]],
  secondary: [[8, 0.15], [11, 0.65], [14, 2.4]],
  tertiary: [[8, 0.15], [11, 0.65], [14, 2.4]],
  minor: [[12, 0.4], [13, 0.9], [14, 1.8]],
  service: [[13, 0.35], [14, 0.8]],
}
const ROAD_CLASSES = Object.keys(ROAD_WIDTHS)
const CASING = 1.4
const CASING_STOPS = [11, 12, 13, 14]

// What MapLibre's own exponential interpolation returns for one class at one
// zoom; 0 before the class's first stop, so a class is simply absent until then.
function widthAt(roadClass: string, zoom: number) {
  const stops = ROAD_WIDTHS[roadClass]
  if (zoom < stops[0][0]) return 0
  if (zoom >= stops[stops.length - 1][0]) return stops[stops.length - 1][1]

  const next = stops.findIndex(([stopZoom]) => stopZoom > zoom)
  const [fromZoom, fromWidth] = stops[next - 1]
  const [toZoom, toWidth] = stops[next]
  const progress = (ROAD_BASE ** (zoom - fromZoom) - 1) / (ROAD_BASE ** (toZoom - fromZoom) - 1)

  return fromWidth + (toWidth - fromWidth) * progress
}

const round = (value: number) => Math.round(value * 100) / 100

// One `interpolate` over zoom whose stop values are a `match` on class. `extra`
// is the casing: the same widths, plus a constant, wherever the stroke exists.
function roadWidth(zooms: number[], extra = 0) {
  const at = (zoom: number) => {
    const widths = ROAD_CLASSES.map((roadClass) => [roadClass, widthAt(roadClass, zoom)] as const).filter(
      ([, width]) => width > 0,
    )
    const match = (scale: number) => [
      'match',
      ['get', 'class'],
      ...widths.flatMap(([roadClass, width]) => [roadClass, round(width * scale + extra)]),
      0,
    ]

    // A motorway ramp is drawn at 40% of its carriageway, and not at all before
    // zoom 11.
    return ['case', ['==', ['get', 'ramp'], 1], zoom < 11 ? 0 : match(0.4), match(1)]
  }

  return ['interpolate', ['exponential', ROAD_BASE], ['zoom'], ...zooms.flatMap((zoom) => [zoom, at(zoom)])]
}

type LooseLayer = { id: string; type: LayerSpecification['type'] } & Record<string, unknown>

// See the header: the validator, not this cast, is what checks a layer.
const layer = (spec: LooseLayer) => spec as unknown as LayerSpecification

const vector = (id: string, sourceLayer: string, type: LayerSpecification['type'], rest: Record<string, unknown>) =>
  layer({ id, type, source: BASEMAP_SOURCE_ID, 'source-layer': sourceLayer, ...rest })

// Meridians and parallels every 30 degrees: what makes the opening view a
// plate of the world rather than land floating on a colour. Sixteen features,
// under a kilobyte, in the style itself — no request.
function graticule() {
  const line = (kind: string, coordinates: [number, number][]) => ({
    type: 'Feature' as const,
    properties: { kind },
    geometry: { type: 'LineString' as const, coordinates },
  })
  const features = []

  for (let lng = -150; lng <= 150; lng += 30) features.push(line('meridian', [[lng, -80], [lng, 84]]))
  for (const lat of [-60, -30, 30, 60]) features.push(line('parallel', [[-180, lat], [180, lat]]))
  features.push(line('equator', [[-180, 0], [180, 0]]))

  return { type: 'FeatureCollection' as const, features }
}

function atlasLayers(palette: AtlasPalette): LayerSpecification[] {
  const label = (color: string, halo = palette.halo, width = 1.4) => ({
    'text-color': color,
    'text-halo-color': halo,
    'text-halo-width': width,
    'text-halo-blur': 0.4,
  })

  // A place name: roman, on its point or beside it.
  const place = (rest: Record<string, unknown>) => ({
    'text-field': NAME,
    'text-font': MAP_FONT_ROMAN,
    'text-variable-anchor-offset': STEP_ASIDE,
    'text-justify': 'auto',
    'text-max-width': 7,
    'symbol-sort-key': ['get', 'rank'],
    ...rest,
  })

  return [
    layer({ id: 'background', type: 'background', paint: { 'background-color': palette.land } }),

    // The HILLSHADE goes here, once the map has drawn: directly over the paper
    // and under everything else, so every layer below this line is drawn on
    // shaded ground.
    slotLayer(SLOT_RELIEF),

    // --- land cover: tints, never colours ------------------------------------
    // `fill-antialias: false` on every one of them: a tint has no edge worth
    // antialiasing, and it skips the outline pass for the most numerous polygons
    // on the map.
    //
    // And none of them opaque. A wooded range drawn at full strength was a flat
    // green shape with the slope it grows on ironed out from under it; at these
    // strengths the relief reads through, and wood is a tone ON a hillside.
    vector('landcover-ice', 'landcover', 'fill', {
      filter: ['==', ['get', 'class'], 'ice'],
      paint: { 'fill-color': palette.ice, 'fill-antialias': false, 'fill-opacity': 0.8 },
    }),
    vector('landcover-wood', 'landcover', 'fill', {
      minzoom: 6,
      filter: ['==', ['get', 'class'], 'wood'],
      paint: {
        'fill-color': palette.wood,
        'fill-antialias': false,
        'fill-opacity': zoomRamp(6, 0, 8, 0.55, 11, 0.7, 14, 0.8),
      },
    }),
    // A faint footprint, so a town exists before its streets do.
    vector('landuse-residential', 'landuse', 'fill', {
      minzoom: 8,
      filter: ['==', ['get', 'class'], 'residential'],
      paint: {
        'fill-color': palette.urban,
        'fill-antialias': false,
        'fill-opacity': zoomRamp(8, 0.35, 12, 0.75),
      },
    }),

    // The atlas's country TINTS go here, and the appellation fills a pointer is
    // tested against: over the land tints and under the water, so the OSM
    // coastline trims a coarse boundary polygon and a lake cuts cleanly out of
    // an appellation.
    slotLayer(SLOT_TINT),

    // The one land-cover class a wine atlas cares about, and the point of the
    // whole plate. OpenMapTiles carries `landuse=vineyard` as landcover class
    // `farmland`, subclass `vineyard`, from zoom 8 up: the Médoc as texture at
    // zoom 9, individual blocks at zoom 13. It costs nothing — the polygons are
    // in tiles the map already downloads.
    //
    // It sits ABOVE the tint slot on purpose. A sage tint under a wine-red wash
    // composites to brown, which is the mud this style exists to get rid of;
    // over the wash it stays the colour it is, and reads as what it is — the
    // vines inside the appellation.
    //
    // A step stronger than it was, and with an edge from where a block is a
    // block: it is the one saturated ground colour on the plate, and on a lit
    // slope the paler sage was the same value as the hill.
    vector('landcover-vineyard', 'landcover', 'fill', {
      minzoom: VINEYARD_MIN_ZOOM,
      filter: ['==', ['get', 'subclass'], 'vineyard'],
      paint: {
        'fill-color': palette.vineyard,
        'fill-antialias': false,
        'fill-opacity': zoomRamp(VINEYARD_MIN_ZOOM, 0.7, 11, 0.95),
      },
    }),
    vector('landcover-vineyard-edge', 'landcover', 'line', {
      minzoom: 10.5,
      filter: ['==', ['get', 'subclass'], 'vineyard'],
      paint: {
        'line-color': palette.vineyardLine,
        'line-width': zoomRamp(10.5, 0.3, 14, 0.8),
        'line-opacity': zoomRamp(10.5, 0, 11.2, 0.75),
      },
    }),

    // The appellation RIBBONS go here: over the vines, so a ribbon is not cut
    // wherever vineyards run up to a boundary, and under the water, so a
    // boundary that follows a coast is trimmed by the surveyed one.
    slotLayer(SLOT_BAND),

    vector('water', 'water', 'fill', {
      filter: NOT_TUNNEL,
      paint: {
        'fill-color': palette.water,
        // The coast hairline, which is the fill's own outline — one layer, not
        // a second pass over the coastline. Not below zoom 3: the planet's
        // ocean polygon has an edge on the antimeridian, and at world zoom the
        // outline would rule a line down the Pacific.
        'fill-outline-color': zoomRamp(2.6, palette.water, 3.4, palette.coast),
      },
    }),
    layer({
      id: 'graticule',
      type: 'line',
      source: GRATICULE_SOURCE_ID,
      maxzoom: 4.5,
      paint: {
        'line-color': palette.graticule,
        'line-width': ['match', ['get', 'kind'], 'equator', 0.7, 0.5],
        'line-opacity': zoomRamp(3, ['match', ['get', 'kind'], 'equator', 0.34, 0.2], 4.5, 0),
      },
    }),
    // Rivers from the first zoom the tiles carry them, which is 3 — they were
    // held back to 8. Rivers are wine geography (the Garonne, the Mosel, the
    // Douro), and on a plate with relief a valley with no river in it is a
    // valley with its reason missing. Smaller waterways still fade in as
    // hairlines towards 11.
    vector('waterway', 'waterway', 'line', {
      minzoom: 3,
      filter: ['all', LINES, NOT_TUNNEL],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': palette.waterLine,
        'line-width': [
          'interpolate',
          ['exponential', 1.4],
          ['zoom'],
          3,
          ['match', ['get', 'class'], 'river', 0.7, 0],
          6,
          ['match', ['get', 'class'], 'river', 1.1, 0],
          9,
          ['match', ['get', 'class'], ['river', 'canal'], 1.35, 0.4],
          11,
          ['match', ['get', 'class'], ['river', 'canal'], 1.9, 0.65],
          14,
          ['match', ['get', 'class'], ['river', 'canal'], 3.2, 1.1],
        ],
      },
    }),

    // --- roads -----------------------------------------------------------------
    // Nothing before zoom 6. Far out a road is one thin warm stroke, darker than
    // the paper. From street zoom it turns into a near-white stroke with a
    // hairline casing, because that is the only treatment that stays legible on
    // flat paper, on a shaded slope AND across an appellation's ribbon: a stroke
    // the same value as any of them disappears into it, and near-white is the
    // only value lighter than all three.
    vector('road-casing', 'transportation', 'line', {
      minzoom: 11,
      filter: ['all', LINES, NOT_TUNNEL, ['match', ['get', 'class'], ROAD_CLASSES, true, false]],
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': palette.roadCasing,
        'line-opacity': zoomRamp(11, 0, 11.6, 1),
        'line-width': roadWidth(CASING_STOPS, CASING),
      },
    }),
    vector('road-minor', 'transportation', 'line', {
      minzoom: 12,
      filter: ['all', LINES, NOT_TUNNEL, ['match', ['get', 'class'], ['minor', 'service'], true, false]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': palette.road, 'line-width': roadWidth([12, 13, 14]) },
    }),
    vector('road-major', 'transportation', 'line', {
      minzoom: 8,
      filter: [
        'all',
        LINES,
        NOT_TUNNEL,
        ['match', ['get', 'class'], ['primary', 'secondary', 'tertiary'], true, false],
      ],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': zoomRamp(11, palette.roadFar, 11.6, palette.road),
        'line-width': roadWidth([8, 11, 14]),
      },
    }),
    // Tunnels stay in this one layer, at 35%: a motorway that vanishes under a
    // mountain and reappears reads as two roads.
    vector('road-motorway', 'transportation', 'line', {
      minzoom: 6,
      filter: ['all', LINES, ['match', ['get', 'class'], ['motorway', 'trunk'], true, false]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': zoomRamp(11, palette.roadMotorwayFar, 11.6, palette.road),
        'line-opacity': ['case', ['==', ['get', 'brunnel'], 'tunnel'], 0.35, 1],
        'line-width': roadWidth([6, 9, 12, 14]),
      },
    }),

    // --- boundaries ------------------------------------------------------------
    vector('boundary-state', 'boundary', 'line', {
      minzoom: 3,
      filter: ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]],
      paint: {
        'line-color': palette.boundaryState,
        'line-dasharray': [3, 2],
        'line-width': zoomRamp(3, 0.4, 8, 0.8, 14, 1.2),
      },
    }),
    vector('boundary-country', 'boundary', 'line', {
      filter: [
        'all',
        ['==', ['get', 'admin_level'], 2],
        ['!=', ['get', 'maritime'], 1],
        ['!=', ['get', 'disputed'], 1],
        ['!', ['has', 'claimed_by']],
      ],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': palette.boundary,
        'line-width': zoomRamp(0, 0.4, 4, 0.8, 10, 1.5),
      },
    }),
    // Dashed, never drawn as settled.
    vector('boundary-disputed', 'boundary', 'line', {
      filter: [
        'all',
        ['==', ['get', 'admin_level'], 2],
        ['!=', ['get', 'maritime'], 1],
        ['==', ['get', 'disputed'], 1],
      ],
      paint: {
        'line-color': palette.boundary,
        'line-dasharray': [2, 2],
        'line-width': zoomRamp(0, 0.4, 4, 0.8, 10, 1.5),
      },
    }),

    // Appellation HAIRLINES go here: over the roads and borders they cross, and
    // under the selection and every label.
    slotLayer(SLOT_LINE),

    // The SELECTED place goes here: the veil over everything that is not it,
    // and its amber ribbon and line. Over every hairline — the veil has to dim
    // the neighbours' edges too — and under every label and dot.
    slotLayer(SLOT_HIGHLIGHT),

    // --- labels: roman for a point, italic for water and summits --------------
    // A river's name: ON the river, every so often along it, and UPRIGHT.
    //
    // MapLibre's own line placement decides WHERE — anchors spaced along the
    // line, thinned by collision like any label, at no cost to this app — and
    // `text-rotation-alignment: viewport` keeps the text from being bent along
    // the line it sits on.
    //
    // Bent along the line is how this layer was written, and it never drew.
    // The tiles' river lines carry a vertex every three to six pixels with
    // turns of 60 to 130 degrees between them; at the 30 degree limit every
    // anchor on every river from zoom 9 to 13 was refused (measured over Napa
    // and the Santa Ynez), and with the limit lifted the letters of "Petaluma
    // River" were set along each kink in turn and could not be read. So the
    // limit is lifted — an upright name does not care how the river bends
    // under it — and the name is not rotated. It loses the angle of the reach
    // and keeps the two things that say "river": the italic and the green.
    //
    // (The other way to get a name along a river is to find each river's
    // straightest reach from the tiles on screen and set one rotated name
    // there. That is a script that runs after every pan and re-cuts a source
    // when it is done, which is a cost on the path this map is tuned along.)
    //
    // An anchor every 350 px, where it was every 500, and a berth of 8 px,
    // where it was 12. A river's name is placed after every appellation's and
    // round every producer's dot, so most of its anchors are refused; at 500
    // a river crossing a tile had one or two to offer, and over Napa the
    // Russian River, Putah Creek and the Petaluma went unnamed. More anchors
    // is more chances at free ground, and the closer berth is what a name set
    // on a line among dots needs. The cost is in the worker, at layout.
    vector('waterway-label', 'waterway', 'symbol', {
      minzoom: 6,
      filter: ['all', LINES, ['match', ['get', 'class'], ['river', 'canal'], true, false]],
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 350,
        'text-field': NAME,
        'text-font': MAP_FONT_ITALIC,
        'text-size': zoomRamp(6, 10.5, 9, 11.5, 12, 13),
        'text-letter-spacing': 0.1,
        'text-max-angle': 360,
        'text-rotation-alignment': 'viewport',
        // Lifted off the line, so a river running across the page passes under
        // its name rather than through it.
        'text-offset': [0, -0.7],
        'text-padding': 8,
      },
      paint: label(palette.labelWater),
    }),
    vector('water-label-line', 'water_name', 'symbol', {
      filter: LINES,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 400,
        'text-field': NAME,
        'text-font': MAP_FONT_ITALIC,
        'text-size': 12.5,
        'text-letter-spacing': 0.1,
      },
      paint: label(palette.labelWater, palette.waterHalo),
    }),
    vector('water-label-point', 'water_name', 'symbol', {
      filter: POINTS,
      layout: {
        'text-field': NAME,
        'text-font': MAP_FONT_ITALIC,
        'text-size': zoomRamp(0, 11, 6, 14.5),
        'text-letter-spacing': 0.14,
        'text-line-height': 1.15,
        'text-max-width': 6,
        'text-padding': 8,
      },
      paint: label(palette.labelWater, palette.waterHalo),
    }),
    vector('road-label', 'transportation_name', 'symbol', {
      minzoom: 12.5,
      filter: [
        'all',
        LINES,
        ['match', ['get', 'class'], ['trunk', 'primary', 'secondary', 'tertiary', 'minor'], true, false],
      ],
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 450,
        'text-field': NAME,
        'text-font': MAP_FONT_ROMAN,
        'text-size': 10,
        'text-padding': 8,
        'text-max-angle': 30,
      },
      paint: label(palette.labelRoad),
    }),
    // Spot heights: a summit, its name and its height, in umber italic.
    //
    // Only from zoom 9.8, which is where the relief has gone soft (it is drawn
    // from level-10 images at most, stretched): the picture stops saying how
    // high the ground is just where a reader choosing between a valley floor
    // and a mountain appellation starts to care, and a figure says it exactly.
    // And only a handful: ranked summits alone, a wide berth round each, the
    // highest placed first. A census of every knoll would be a second map.
    vector('peak', 'mountain_peak', 'symbol', {
      minzoom: 9.8,
      filter: [
        'all',
        ['has', 'ele'],
        ['has', 'name'],
        ['<=', ['get', 'rank'], ['step', ['zoom'], 1, 11.5, 2, 13, 4]],
      ],
      layout: {
        'icon-image': PEAK_IMAGE_ID,
        'text-field': ['format', NAME, {}, '\n', {}, ['concat', ['to-string', ['get', 'ele']], ' m'], { 'font-scale': 0.92 }],
        'text-font': MAP_FONT_ITALIC,
        'text-size': zoomRamp(9.8, 10, 14, 11.5),
        'text-anchor': 'top',
        'text-offset': [0, 0.5],
        'text-max-width': 8,
        'text-line-height': 1.1,
        'text-padding': 30,
        'symbol-sort-key': ['-', 0, ['to-number', ['get', 'ele'], 0]],
      },
      paint: {
        ...label(palette.labelPeak, palette.halo, 1.2),
        'text-opacity': zoomRamp(9.8, 0, 10.1, 1),
        'icon-opacity': zoomRamp(9.8, 0, 10.1, 1),
      },
    }),
    vector('place-hamlet', 'place', 'symbol', {
      minzoom: 12.5,
      filter: ['match', ['get', 'class'], ['hamlet', 'suburb', 'quarter', 'neighbourhood'], true, false],
      layout: place({ 'text-size': 10.5, 'text-padding': 6 }),
      paint: label(palette.labelVillage),
    }),
    // From zoom 10: villages are the unit of a European appellation.
    vector('place-village', 'place', 'symbol', {
      minzoom: 10,
      filter: ['==', ['get', 'class'], 'village'],
      layout: place({ 'text-size': zoomRamp(10, 10.5, 14, 12.5), 'text-padding': 6 }),
      paint: label(palette.labelVillage),
    }),
    vector('place-town', 'place', 'symbol', {
      minzoom: 7,
      filter: ['==', ['get', 'class'], 'town'],
      layout: place({ 'text-size': zoomRamp(7, 10.5, 11, 12.5, 14, 14), 'text-padding': 6 }),
      paint: label(palette.labelTown),
    }),
    vector('place-state', 'place', 'symbol', {
      minzoom: 4,
      maxzoom: 8,
      filter: [
        'all',
        ['match', ['get', 'class'], ['state', 'province'], true, false],
        // Evaluated at integer zooms only, which is fine for a label budget: a
        // continent view names the big first-order divisions and nothing else.
        ['step', ['zoom'], ['<=', ['get', 'rank'], 2], 5, ['<=', ['get', 'rank'], 4], 6, true],
      ],
      layout: {
        'text-field': NAME,
        'text-font': MAP_FONT_ROMAN,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.2,
        'text-size': zoomRamp(4, 9, 8, 11.5),
        'text-max-width': 8,
        'text-padding': 10,
        'symbol-sort-key': ['get', 'rank'],
      },
      paint: label(palette.labelState),
    }),
    vector('place-city', 'place', 'symbol', {
      minzoom: 3,
      filter: ['==', ['get', 'class'], 'city'],
      layout: place({ 'text-size': zoomRamp(3, 10.5, 7, 12.5, 11, 15.5), 'text-padding': 8 }),
      paint: label(palette.labelCity),
    }),
    // Rank 1 only below zoom 2, rank 2 and better below zoom 3: the opening
    // world view names the countries a reader could find unaided and no more.
    vector('place-country', 'place', 'symbol', {
      maxzoom: 8,
      filter: [
        'all',
        ['==', ['get', 'class'], 'country'],
        ['step', ['zoom'], ['<=', ['get', 'rank'], 1], 2, ['<=', ['get', 'rank'], 2], 3, true],
      ],
      layout: {
        'text-field': NAME,
        'text-font': MAP_FONT_ROMAN,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.16,
        'text-size': zoomRamp(
          1,
          ['case', ['<=', ['get', 'rank'], 1], 10, 9],
          4,
          ['case', ['<=', ['get', 'rank'], 1], 13, 11],
          7,
          ['case', ['<=', ['get', 'rank'], 1], 15, 13],
        ),
        'text-max-width': 7,
        'text-padding': 10,
        'symbol-sort-key': ['get', 'rank'],
      },
      paint: label(palette.labelCountry),
    }),

    // The winery DOTS go here, and the room each keeps: over every name above
    // this line, which is placed after them and so steps aside for a dot, and
    // under the appellations' names, which are placed before them and do not.
    slotLayer(SLOT_DOT),

    // Appellation NAMES go here: above every basemap label, so an appellation
    // wins a collision against the town it surrounds, and above the dots.
    // Nothing in the style itself is above this.
    slotLayer(SLOT_LABEL),
  ]
}

// The two static cuts of Fraunces the map is lettered in (app/frontend/fonts,
// and `MAP_FONT_ROMAN` in map_config.ts for why the stacks keep Noto's names).
// Keyed by fontstack name. The pair replaces the two glyph ranges every view
// asked the endpoint for and adds nothing to them. MapLibre would fetch each
// file the first time a label needs a glyph from it; the map asks for both as
// soon as a style has loaded instead (`primeLettering` in
// components/map/use_maplibre.ts), so a view with no italic on it fetches the
// italic all the same. A file that fails to load is skipped
// and its codepoints fall through to the endpoint: the map is lettered in Noto
// Sans rather than not at all.
const FONT_FACES: StyleSpecification['font-faces'] = {
  [MAP_FONT_ROMAN[0]]: [{ url: romanUrl, 'unicode-range': MAP_FONT_RANGES }],
  [MAP_FONT_ITALIC[0]]: [{ url: italicUrl, 'unicode-range': MAP_FONT_RANGES }],
}

export function atlasStyle(palette: AtlasPalette = ATLAS_PALETTE): StyleSpecification {
  return {
    version: 8,
    name: 'Winemap Atlas',
    sources: {
      // The TileJSON, not a hand-written `tiles` template. The tile path
      // carries a weekly build id and is served with a ten-year cache header;
      // OpenFreeMap's `/planet/latest/…` alias answers, but with the same
      // header, so a browser would pin whatever "latest" was on first visit and
      // later mix builds across tile edges. The TileJSON is 1.8 KB and cached
      // for a day.
      [BASEMAP_SOURCE_ID]: { type: 'vector', url: `${OPENFREEMAP}/planet` },
      [GRATICULE_SOURCE_ID]: { type: 'geojson', data: graticule() },
    },
    glyphs: GLYPHS,
    'font-faces': FONT_FACES,
    layers: atlasLayers(palette),
  }
}

export const ATLAS_STYLE: StyleSpecification = atlasStyle()

/**
 * Paper and nothing else: what the map falls back to when the basemap's own
 * style cannot be drawn at all.
 *
 * With the style bundled that is nearly unreachable — there is no style request
 * left to fail, and when the tiles are down the atlas style above already IS
 * paper with the overlays on it. It stays for the case it was written for (an
 * error before any style has drawn), and it carries three things so that case
 * takes the same code path as the normal one:
 *
 * - **the seven slots, in the same order.** Every atlas layer names one, and
 *   `addLayer` before a layer that does not exist adds nothing. The relief's
 *   among them: it is this app's own and does not go down with the basemap.
 * - **the glyph endpoint.** A symbol layer in a style with no `glyphs` is a
 *   validation error, so without it the fallback lost every appellation name
 *   and every cluster count along with the basemap. If the endpoint is down too,
 *   MapLibre draws the text locally.
 * - **the two font files**, which are this app's own too, so the names on a
 *   dead basemap are still in the atlas's face.
 */
export const PLAIN_STYLE: StyleSpecification = {
  version: 8,
  name: 'Winemap Atlas (plain)',
  sources: {},
  glyphs: GLYPHS,
  'font-faces': FONT_FACES,
  layers: [
    layer({ id: 'plain-background', type: 'background', paint: { 'background-color': PLAIN_BACKGROUND_COLOR } }),
    slotLayer(SLOT_RELIEF),
    slotLayer(SLOT_TINT),
    slotLayer(SLOT_BAND),
    slotLayer(SLOT_LINE),
    slotLayer(SLOT_HIGHLIGHT),
    slotLayer(SLOT_DOT),
    slotLayer(SLOT_LABEL),
  ],
}
